import { ChevronDown, X } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { cn } from '../lib/cn';

export type SearchSelectProps<T> = {
    value: T | null;
    onChange: (item: T | null) => void;
    /** Current result set for the latest query — owned by the parent's hook. */
    items: T[];
    loading?: boolean;
    /** Debounced query text; the parent runs its search off this. */
    onQueryChange: (query: string) => void;
    /** Open-state notifier so the parent can `skip` its query while closed. */
    onOpenChange?: (open: boolean) => void;
    getKey: (item: T) => string | number;
    getLabel: (item: T) => string;
    renderItem?: (item: T) => ReactNode;
    placeholder?: string;
    emptyText?: string; // shown when a query is typed but nothing matches
    promptText?: string; // shown before the operator types anything
    disabled?: boolean;
    ariaLabel?: string;
};

// Generic server-search single-select. Manages the input / dropdown / debounce
// and hands the debounced query back to the parent (which owns the data hook),
// so the same interaction works for any searchable entity (users, orders, ...).
export function SearchSelect<T>({
    value,
    onChange,
    items,
    loading,
    onQueryChange,
    onOpenChange,
    getKey,
    getLabel,
    renderItem,
    placeholder,
    emptyText = 'Niciun rezultat',
    promptText = 'Scrie pentru a cauta',
    disabled,
    ariaLabel,
}: SearchSelectProps<T>) {
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState('');
    const ref = useRef<HTMLDivElement>(null);
    const listId = useId();

    // Stash the callback so the debounce effect doesn't re-run when a parent
    // passes a fresh function identity each render.
    const onQueryChangeRef = useRef(onQueryChange);
    onQueryChangeRef.current = onQueryChange;

    const setOpenState = useCallback(
        (next: boolean) => {
            setOpen(next);
            onOpenChange?.(next);
        },
        [onOpenChange],
    );

    useEffect(() => {
        const t = window.setTimeout(() => onQueryChangeRef.current(input.trim()), 250);
        return () => window.clearTimeout(t);
    }, [input]);

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) setOpenState(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open, setOpenState]);

    const display = open ? input : value ? getLabel(value) : '';

    return (
        <div ref={ref} className="relative w-full">
            <input
                type="text"
                role="combobox"
                aria-expanded={open}
                aria-controls={listId}
                aria-label={ariaLabel}
                value={display}
                disabled={disabled}
                placeholder={placeholder}
                onFocus={() => setOpenState(true)}
                onChange={(e) => {
                    setInput(e.target.value);
                    setOpenState(true);
                }}
                className="flex h-10 w-full rounded-md border border-solid border-gray-300 bg-white py-2 pl-3 pr-9 text-sm text-gray-900 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-transparent disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
            />
            {value && !disabled ? (
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                        onChange(null);
                        setInput('');
                    }}
                    aria-label="Sterge selectia"
                    className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                >
                    <X aria-hidden="true" className="h-4 w-4" />
                </button>
            ) : (
                <ChevronDown
                    aria-hidden="true"
                    className={cn(
                        'pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition-transform',
                        open && 'rotate-180',
                    )}
                />
            )}
            {open && (
                <ul
                    id={listId}
                    role="listbox"
                    className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-md border border-solid border-gray-200 bg-white py-1 text-sm shadow-lg dark:border-gray-700 dark:bg-gray-800"
                >
                    {loading ? (
                        <li className="px-3 py-2 text-gray-500 dark:text-gray-400">Se cauta...</li>
                    ) : items.length === 0 ? (
                        <li className="px-3 py-2 text-gray-500 dark:text-gray-400">
                            {input.trim() ? emptyText : promptText}
                        </li>
                    ) : (
                        items.map((item) => {
                            const key = getKey(item);
                            const selected = value != null && getKey(value) === key;
                            return (
                                <li
                                    key={key}
                                    role="option"
                                    aria-selected={selected}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        onChange(item);
                                        setOpenState(false);
                                        setInput('');
                                    }}
                                    className={cn(
                                        'cursor-pointer px-3 py-2 text-gray-900 hover:bg-blue-50 hover:text-blue-700 dark:text-gray-100 dark:hover:bg-blue-900/40 dark:hover:text-blue-200',
                                        !renderItem && 'truncate',
                                        selected && 'font-medium',
                                    )}
                                >
                                    {renderItem ? renderItem(item) : getLabel(item)}
                                </li>
                            );
                        })
                    )}
                </ul>
            )}
        </div>
    );
}
